package com.kinoshazam.screens.details.medium

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyListScope
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.layout.LayoutCoordinates
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.kinoshazam.screens.details.BannerContainer
import com.kinoshazam.screens.details.TrailerButton
import com.kinoshazam.screens.details.TrailerLayer
import com.kinoshazam.screens.details.rectInside
import com.kinoshazam.screens.details.syncTrailerActions
import com.kinoshazam.screens.details.syncTrailerCaption
import com.kinoshazam.screens.details.rememberTrailerCovered
import com.kinoshazam.screens.details.rememberTrailerExpansion
import com.kinoshazam.screens.details.videoRatingLabel
import com.kinoshazam.screens.details.videoYearLabel
import com.kinoshazam.uiKit.components.expandableDescription.ExpandableDescription
import com.kinoshazam.uiKit.components.cells.cellItem.CellItem
import com.kinoshazam.uiKit.components.cells.cellItem.CellItemState
import com.kinoshazam.uiKit.components.selectableText.SelectableTextState
import com.kinoshazam.uiKit.components.selectableText.SelectableTextView
import com.kinoshazam.uiKit.components.textLabel.TextLabel
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.localizedGenreName
import com.kinoshazam.uiKit.components.videoItem.MediumVideoCardWidthCompact
import com.kinoshazam.uiKit.components.videoItem.MediumVideoItem
import com.kinoshazam.uiKit.components.videoItem.MovieCastItem
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.episodeItem.EpisodeView
import com.kinoshazam.uiKit.episodeItem.EpisodeViewDefaults
import com.kinoshazam.uiKit.extension.disable
import com.kinoshazam.uiKit.extension.thenIf
import com.kinoshazam.uiKit.theme.BoldExtraLarge
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.Medium
import com.kinoshazam.uiKit.theme.MediumMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import com.kinoshazam.view.ErrorView
import features.videoDetails.DetailsMVIAction
import features.videoDetails.VideoDetailsState
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.close
import kinoshazam.resources.generated.resources.details_cast
import kinoshazam.resources.generated.resources.details_watch_also
import kinoshazam.resources.generated.resources.episode
import kinoshazam.resources.generated.resources.episode_error
import kinoshazam.resources.generated.resources.error
import kinoshazam.resources.generated.resources.favorite
import kinoshazam.resources.generated.resources.favourites
import kinoshazam.resources.generated.resources.fill_favorite
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.play
import kinoshazam.resources.generated.resources.repeat
import kinoshazam.resources.generated.resources.season
import kinoshazam.resources.generated.resources.trailer
import com.kinoshazam.screens.details.watchCtaLabel
import com.kinoshazam.uiKit.trailer.TrailerControls
import com.kinoshazam.uiKit.trailer.trailerHidesUnderlyingContent
import models.VideoDetails
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
fun MediumVideoDetailsContent(
    state: VideoDetailsState,
    onAction: (DetailsMVIAction) -> Unit,
    modifier: Modifier = Modifier
) {
    val expansion = rememberTrailerExpansion()
    var trailerFailed by remember { mutableStateOf(false) }
    val trailerAvailable = !state.videoInfo?.trailerUrl.isNullOrBlank() && !trailerFailed
    val scrollState = rememberScrollState()
    val density = LocalDensity.current
    val bannerChrome = TrailerControls.banner
    bannerChrome.title = state.videoInfo?.title.orEmpty()
    bannerChrome.rating = videoRatingLabel(state.videoInfo).orEmpty()
    bannerChrome.countries = state.videoInfo?.countries?.map { gen ->
        gen.name?.replaceFirstChar { it.uppercase() }
    }?.joinToString { it.orEmpty() }.orEmpty()
    bannerChrome.genres = state.videoInfo?.genres?.map { gen ->
        localizedGenreName(gen.name, gen.icon)
    }?.joinToString().orEmpty()
    bannerChrome.year = videoYearLabel(state.videoInfo)
    bannerChrome.centered = true
    bannerChrome.playLabel = watchCtaLabel(state.watchTarget)
    bannerChrome.trailerLabel = stringResource(Res.string.trailer)
    bannerChrome.favoriteLabel = stringResource(Res.string.favorite)
    bannerChrome.showPlay = state.features.watchVideo
    bannerChrome.showTrailer = trailerAvailable
    bannerChrome.favorite = state.isFavorite
    bannerChrome.onTrailer = expansion::toggle
    bannerChrome.onFavorite = {
        state.videoInfo?.id?.let { videoId ->
            onAction(DetailsMVIAction.AddToFavorite(videoId))
        }
    }
    bannerChrome.onPlay = {
        val imdbId = state.videoInfo?.imdbId
        val url = if (state.videoInfo?.isTvShow == true) {
            val season = state.selectedSeason?.seasonNumber
            val episode = state.selectedEpisode?.episodeNumber
            "https://api.tobaco.ws/embed/imdb/${imdbId}?season=$season&episode=$episode&host=filmot.site&episodesOpen=false"
        } else {
            "https://api.tobaco.ws/embed/imdb/${imdbId}?host=filmot.site"
        }
        onAction(DetailsMVIAction.OnPlayClicked(url))
    }
    val hideUnderTrailer = trailerAvailable && trailerHidesUnderlyingContent
    val coverTrailer = rememberTrailerCovered(if (trailerAvailable) state.videoInfo?.trailerUrl else null)
    var stage by remember { mutableStateOf<LayoutCoordinates?>(null) }
    var banner by remember { mutableStateOf<Rect?>(null) }
    val hero = remember { arrayOfNulls<LayoutCoordinates>(1) }
    val caption = remember { arrayOfNulls<LayoutCoordinates>(1) }
    val actions = remember { arrayOfNulls<LayoutCoordinates>(1) }
    fun syncCaption(next: LayoutCoordinates? = null) {
        if (next != null) caption[0] = next
        val parent = stage ?: return
        val place = banner ?: return
        val child = caption[0] ?: return
        syncTrailerCaption(child, parent, place, density)
    }
    fun syncActions(next: LayoutCoordinates? = null) {
        if (next != null) actions[0] = next
        val parent = stage ?: return
        val place = banner ?: return
        val child = actions[0] ?: return
        syncTrailerActions(child, parent, place, density)
    }
    fun syncHero(next: LayoutCoordinates? = null) {
        if (next != null) hero[0] = next
        val parent = stage ?: return
        val child = hero[0] ?: return
        child.rectInside(parent)?.let { banner = it }
        syncCaption()
        syncActions()
    }
    Scaffold(
        modifier = modifier
            .fillMaxSize()
            .background(UiKitTheme.colors.transparent),
        containerColor = UiKitTheme.colors.transparent,
        content = { _ ->
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .onGloballyPositioned {
                        stage = it
                        syncHero()
                        syncCaption()
                        syncActions()
                    },
            ) {
                DialogContent(
                    onClose = {
                        TrailerControls.release()
                        onAction(DetailsMVIAction.BackClicked)
                    }
                ) {
                    when {
                        state.isLoading -> LoadingScreen()
                        state.isError -> ErrorScreen(onAction)
                        else -> ContentScreen(
                            state = state,
                            scrollState = scrollState,
                            onMediaPlaced = { syncHero(it) },
                            onCaptionPlaced = { syncCaption(it) },
                            onActionsPlaced = { syncActions(it) },
                            hideUnderTrailer = hideUnderTrailer,
                            coverTrailer = coverTrailer,
                            trailerAvailable = trailerAvailable,
                            onTrailer = expansion::toggle,
                            onAction = onAction,
                        )
                    }
                }
                TrailerLayer(
                    trailerUrl = if (trailerFailed) null else state.videoInfo?.trailerUrl,
                    expansion = expansion,
                    banner = banner,
                    scrollState = scrollState,
                    pullToExpand = false,
                    collapsedCorner = 16.dp,
                    onBack = {
                        TrailerControls.release()
                        onAction(DetailsMVIAction.BackClicked)
                    },
                    onUnavailable = {
                        trailerFailed = true
                        expansion.collapse()
                    },
                )
            }
        }
    )
}

@Composable
private fun LoadingScreen() {
    Box(
        modifier = Modifier.fillMaxSize(),
    ) {
        CircularProgressIndicator(
            modifier = Modifier.size(24.dp).align(Alignment.Center),
            color = UiKitTheme.colors.color_1,
            strokeWidth = 2.dp,
        )
    }
}

@Composable
private fun ErrorScreen(
    onAction: (DetailsMVIAction) -> Unit,
) {
    Box {
        ErrorView(
            textError = stringResource(Res.string.error),
            textAction = stringResource(Res.string.repeat),
            onAction = { onAction(DetailsMVIAction.Reload) }
        )
    }
}

@Composable
private fun ContentScreen(
    state: VideoDetailsState,
    scrollState: ScrollState,
    onMediaPlaced: (LayoutCoordinates) -> Unit,
    onCaptionPlaced: (LayoutCoordinates) -> Unit,
    onActionsPlaced: (LayoutCoordinates) -> Unit,
    hideUnderTrailer: Boolean,
    coverTrailer: Boolean,
    trailerAvailable: Boolean,
    onTrailer: () -> Unit,
    onAction: (DetailsMVIAction) -> Unit,
) {
    Box {
        BannerContainer(
            imageUrl = state.videoInfo?.backdropUrl ?: state.videoInfo?.imageUrl,
            showPoster = !trailerAvailable || coverTrailer,
            onPlaced = onMediaPlaced,
        )
        Column(
            modifier = Modifier
                .verticalScroll(scrollState),
        ) {
            VideoInfoContent(
                videoInfo = state.videoInfo,
                isFavorite = state.isFavorite,
                watchVideo = state.features.watchVideo,
                hasTrailer = trailerAvailable,
                selectedSeason = state.selectedSeason,
                selectedEpisode = state.selectedEpisode,
                watchTarget = state.watchTarget,
                onCaptionPlaced = onCaptionPlaced,
                onActionsPlaced = onActionsPlaced,
                hideUnderTrailer = hideUnderTrailer,
                onTrailer = onTrailer,
                onAction = onAction,
            )

            Spacer(modifier = Modifier.height(32.dp))

            if (state.videoInfo?.isTvShow == true) SeriesContent(state, onAction)

            if (!state.videoInfo?.description.isNullOrEmpty()) {
                Spacer(modifier = Modifier.height(32.dp))

                ExpandableDescription(
                    text = state.videoInfo?.description.orEmpty(),
                    expanded = state.isFullDescription,
                    onToggle = { onAction(DetailsMVIAction.ClickMore) },
                )
            }

            Spacer(modifier = Modifier.height(32.dp))
            Spacer(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(1.dp)
                    .padding(horizontal = 16.dp)
                    .background(color = UiKitTheme.colors.color_4)
            )

            if (!state.videoCast?.actors.isNullOrEmpty()) {
                CellItem(
                    state = CellItemState(
                        title = TextLabelState(
                            text = stringResource(Res.string.details_cast),
                            color = UiKitTheme.colors.color_2,
                        )
                    )
                )

                LazyRow(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    itemsIndexed(state.videoCast?.actors.orEmpty()) { index, it ->
                        MovieCastItem(
                            actor = it,
                            onClick = { onAction(DetailsMVIAction.OnActorClicked(it.id)) },
                            modifier = when (index) {
                                0 -> Modifier.padding(start = 16.dp)
                                state.videoCast?.actors?.size?.minus(1) -> Modifier.padding(end = 16.dp)
                                else -> Modifier.padding()
                            }
                        )
                    }
                }
            }

            if (!state.similarMovie.isNullOrEmpty()) {
                CellItem(
                    state = CellItemState(
                        title = TextLabelState(
                            text = stringResource(Res.string.details_watch_also),
                            color = UiKitTheme.colors.color_2,
                        )
                    )
                )

                LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    contentPadding = PaddingValues(horizontal = 16.dp)
                ) {
                    itemsIndexed(state.similarMovie.orEmpty()) { _, it ->
                        MediumVideoItem(
                            state = VideoState(
                                id = it.id ?: -1,
                                title = it.title.orEmpty(),
                                image = it.imageUrl,
                                placeHolder = painterResource(Res.drawable.movie),
                                placeHolderColor = UiKitTheme.colors.color_5,
                                onAction = { id ->
                                    onAction(DetailsMVIAction.OnVideoClicked(id))
                                }
                            ),
                            width = MediumVideoCardWidthCompact,
                            modifier = Modifier.padding(bottom = 16.dp),
                        )
                    }
                }
            }

            Spacer(modifier = Modifier.height(48.dp))
        }
    }
}

@Composable
private fun VideoInfoContent(
    videoInfo: VideoDetails?,
    isFavorite: Boolean,
    watchVideo: Boolean,
    hasTrailer: Boolean,
    selectedSeason: VideoDetails.Season?,
    selectedEpisode: VideoDetails.Episode?,
    watchTarget: features.watch.model.WatchTarget?,
    onCaptionPlaced: (LayoutCoordinates) -> Unit,
    onActionsPlaced: (LayoutCoordinates) -> Unit,
    hideUnderTrailer: Boolean,
    onTrailer: () -> Unit,
    onAction: (DetailsMVIAction) -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 16.dp, vertical = 16.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {

        Spacer(modifier = Modifier.statusBarsPadding().height(135.dp))

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .onGloballyPositioned(onCaptionPlaced)
                .graphicsLayer { alpha = if (hideUnderTrailer) 0f else 1f },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            TextLabel(
                state = TextLabelState(
                    text = videoInfo?.title.orEmpty(),
                    textStyle = BoldExtraLarge,
                    color = UiKitTheme.colors.color_2,
                )
            )

            Spacer(modifier = Modifier.height(4.dp))

            TextLabel(
                state = TextLabelState(
                    text = videoInfo?.genres?.map { gen ->
                        localizedGenreName(gen.name, gen.icon)
                    }?.joinToString().orEmpty(),
                    textStyle = MediumMedium,
                    color = UiKitTheme.colors.color_2,
                )
            )

            val releaseYear = videoYearLabel(videoInfo)
            if (releaseYear.isNotBlank()) {
                Spacer(modifier = Modifier.height(4.dp))
                TextLabel(
                    state = TextLabelState(
                        text = releaseYear,
                        textStyle = MediumMedium,
                        color = UiKitTheme.colors.color_2,
                    )
                )
            }

            videoRatingLabel(videoInfo)?.let { rating ->
                Spacer(modifier = Modifier.height(20.dp))
                TextLabel(
                    state = TextLabelState(
                        text = rating,
                        textStyle = MediumMedium,
                        color = UiKitTheme.colors.color_1,
                    )
                )
            }

            Spacer(modifier = Modifier.height(4.dp))

            TextLabel(
                state = TextLabelState(
                    text = videoInfo?.countries?.map { gen ->
                        gen.name?.replaceFirstChar { it.uppercase() }
                    }?.joinToString { it.orEmpty() }.orEmpty(),
                    textStyle = MediumMedium,
                    color = UiKitTheme.colors.color_2,
                )
            )
        }

        Spacer(modifier = Modifier.height(32.dp))

        val playInteraction = remember { MutableInteractionSource() }
        val isPlayHovered by playInteraction.collectIsHoveredAsState()
        val trailerInteraction = remember { MutableInteractionSource() }
        val isTrailerHovered by trailerInteraction.collectIsHoveredAsState()
        val favoriteInteraction = remember { MutableInteractionSource() }
        val isFavoriteHovered by favoriteInteraction.collectIsHoveredAsState()

        FlowRow(
            horizontalArrangement = Arrangement.spacedBy(4.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp),
            modifier = Modifier
                .onGloballyPositioned(onActionsPlaced)
                .graphicsLayer { alpha = if (hideUnderTrailer) 0f else 1f }
                .padding(horizontal = 16.dp)
        ) {
            if (watchVideo) {
                Button(
                    modifier = Modifier.height(48.dp),
                    interactionSource = playInteraction,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = if (isPlayHovered) {
                            UiKitTheme.colors.color_23
                        } else {
                            UiKitTheme.colors.color_1
                        }
                    ),
                    onClick = {
                        val imdbId = videoInfo?.imdbId
                        // TODO: get url(html code) from settings
                        val url = if (videoInfo?.isTvShow == true) {
                            val season = selectedSeason?.seasonNumber
                            val episode = selectedEpisode?.episodeNumber
                            "https://api.tobaco.ws/embed/imdb/${imdbId}?season=$season&episode=$episode&host=filmot.site&episodesOpen=false"
                        } else {
                            "https://api.tobaco.ws/embed/imdb/${imdbId}?host=filmot.site"
                        }
                        onAction(DetailsMVIAction.OnPlayClicked(url))
                    }
                ) {
                    Image(
                        painter = painterResource(Res.drawable.play),
                        colorFilter = ColorFilter.tint(UiKitTheme.colors.color_7),
                        contentDescription = null,
                    )

                    Spacer(modifier = Modifier.width(6.dp))

                    val watch = watchCtaLabel(watchTarget)
                    TextLabel(
                        state = TextLabelState(
                            text = watch,
                            textStyle = BoldMedium,
                            color = UiKitTheme.colors.color_7,
                        )
                    )
                }
            }

            if (hasTrailer) {
                TrailerButton(
                    onClick = onTrailer,
                    interactionSource = trailerInteraction,
                    containerColor = if (isTrailerHovered) {
                        UiKitTheme.colors.color_1
                    } else {
                        UiKitTheme.colors.color_3_80
                    },
                )
            }

            Button(
                modifier = Modifier.height(48.dp)
                    .thenIf(Modifier.width(48.dp), watchVideo),
                interactionSource = favoriteInteraction,
                contentPadding = if (watchVideo) {
                    PaddingValues()
                } else {
                    ButtonDefaults.ContentPadding
                },
                colors = ButtonDefaults.buttonColors(
                    containerColor = when {
                        isFavorite || isFavoriteHovered -> UiKitTheme.colors.color_1
                        else -> UiKitTheme.colors.color_3_80
                    }
                ),
                onClick = {
                    videoInfo?.id?.let { videoId ->
                        onAction(DetailsMVIAction.AddToFavorite(videoId))
                    }
                }
            ) {
                if (!watchVideo) {
                    TextLabel(
                        state = TextLabelState(
                            text = stringResource(Res.string.favorite),
                            textStyle = BoldMedium,
                            color = UiKitTheme.colors.color_7,
                        )
                    )

                    Spacer(modifier = Modifier.width(6.dp))
                }

                Image(
                    painter = painterResource(if (isFavorite) Res.drawable.fill_favorite else Res.drawable.favourites),
                    colorFilter = ColorFilter.tint(UiKitTheme.colors.color_7),
                    contentDescription = null,
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.padding()
                )
            }
        }
    }
}

@Composable
private fun SeriesContent(
    state: VideoDetailsState,
    onAction: (DetailsMVIAction) -> Unit,
) {
    val tvInfo = state.videoInfo ?: return
    Column {
        LazyRow(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            contentPadding = PaddingValues(horizontal = 16.dp)
        ) {
            itemsIndexed(tvInfo.seasons ?: emptyList()) { index, it ->
                SelectableTextView(
                    state = SelectableTextState(
                        isSelected = it.seasonNumber == state.selectedSeason?.seasonNumber,
                        title = it.title ?: stringResource(Res.string.season, it.seasonNumber),
                    )
                ) {
                    onAction(DetailsMVIAction.OnSeasonClicked(tvInfo.id ?: -1, it))
                }
            }
        }
        Spacer(modifier = Modifier.height(16.dp))
        if (state.selectedSeason?.episodes.isNullOrEmpty() && !state.isEpisodesLoading) {
            EpisodesError()
        } else {
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(10.dp),
                contentPadding = PaddingValues(horizontal = 16.dp),
            ) {
                if (state.isEpisodesLoading) {
                    EpisodesSkeleton()
                } else {
                    itemsIndexed(state.selectedSeason?.episodes ?: emptyList()) { index, it ->
                        EpisodeView(
                            isSelected = index + 1 == state.selectedEpisode?.episodeNumber,
                            title = stringResource(Res.string.episode, index + 1),
                            defaults = EpisodeViewDefaults(
                                cornerRadius = 12.dp,
                                borderWidth = 4.dp,
                            )
                        ) {
                            onAction(
                                DetailsMVIAction.OnEpisodeClicked(tvInfo.id ?: -1, it)
                            )
                            // TODO: open episodes info if need
                        }
                    }
                }
            }
        }
    }
}

private fun LazyListScope.EpisodesSkeleton() =
    items(5) {
        EpisodeView(isLoading = true)
    }

@Composable
private fun EpisodesError() {
    Box(
        contentAlignment = Alignment.Center,
        modifier = Modifier
            .fillMaxWidth()
            .size(90.dp)
    ) {
        TextLabel(
            state = TextLabelState(
                text = stringResource(Res.string.episode_error),
                color = UiKitTheme.colors.color_2,
                textStyle = Medium,
            )
        )
    }
}

@Composable
fun DialogContent(
    onClose: () -> Unit,
    dialogWidth: Dp = 500.dp,
    fillMaxHeight: Boolean = false,
    contentAlignment: Alignment = Alignment.TopCenter,
    content: @Composable () -> Unit,
) {
    val closeInteraction = remember { MutableInteractionSource() }
    val isCloseHovered by closeInteraction.collectIsHoveredAsState()

    Box(
        modifier = Modifier
            .fillMaxSize()
            .clickable(
                indication = null,
                interactionSource = remember { MutableInteractionSource() }) {
                onClose()
            }
            .background(UiKitTheme.colors.color_3_80.copy(alpha = 0.8f))
            .padding(20.dp),
        contentAlignment = contentAlignment,
    ) {
        Image(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .clickable(
                    interactionSource = closeInteraction,
                    indication = null,
                    onClick = onClose,
                ),
            painter = painterResource(Res.drawable.close),
            colorFilter = ColorFilter.tint(
                if (isCloseHovered) UiKitTheme.colors.color_22 else UiKitTheme.colors.color_1
            ),
            contentDescription = null
        )
        Box(
            modifier = Modifier
                .width(dialogWidth)
                .then(if (fillMaxHeight) Modifier.fillMaxHeight() else Modifier)
                .clip(RoundedCornerShape(16.dp))
                .background(UiKitTheme.colors.color_3)
                .disable()
        ) {
            content()
        }
    }
}